// i18n
import { useTranslation } from 'react-i18next';

// types
import { CategoryAllocation, ComponentAllocation, Loan } from '../../../../@types';

// ui
import { Amount } from '../../../ui/Amount/Amount';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// mui
import { Grid, LinearProgress, Typography } from '@mui/material';

// utils
import { formatPercentage } from '../../../../utils/format';
import { formatCurrency } from '../../../../utils/currency';

// props
export interface TableBasicAllocationsProps extends Omit<TableProps, 'headers'> {
	data: CategoryAllocation[] | ComponentAllocation[];
}

export const TableBasicAllocations = ({ data = [], ...props }: TableBasicAllocationsProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const headers: TableHeader[] = [
		{
			label: t('source'),
			render: (loan: Loan) => {
				return <Typography fontWeight={600}>{loan.code}</Typography>;
			},
			value: 'loan',
		},
		{
			label: t('modified'),
			render: (modified: string) => {
				return <Time date={modified} />;
			},
			value: 'modified',
		},
		{
			label: t('allocation'),
			render: (
				amount: number,
				header: TableHeader,
				allocation: CategoryAllocation | ComponentAllocation
			) => {
				return (
					<Grid alignItems="center" container spacing={4} wrap="nowrap">
						<Grid item xs="auto">
							<Amount
								conversions={allocation.conversions || []}
								currency={allocation.currency}
								value={amount}
							/>
						</Grid>
						{allocation.utilization && (
							<Grid item xs={true}>
								<Grid container>
									<Grid item xs={12}>
										<LinearProgress
											color="primary"
											sx={{
												width: '100%',
											}}
											value={allocation.utilization.value || 0}
											variant="determinate"
										/>
									</Grid>

									<Grid item xs={12}>
										<Grid container justifyContent="flex-end">
											<Grid item>
												<Typography component="span" fontWeight={600}>
													{formatPercentage(allocation.utilization.value)}
												</Typography>
												<Typography color="brand.grey.500" component="span">
													{` ${t('of')} `}
													{formatCurrency({
														currency: allocation.utilization.currency,
														notation: 'compact',
														value: allocation.utilization.amount,
													})}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				);
			},
			value: 'amount',
		},
	];

	return <Table color="grey" {...props} data={data} headers={headers} />;
};
